// Cards on the table (docs/04 §3 "the desk is the diegetic UI surface"): the Arcanium display with
// the spell deck to the left of the Arcanium; the Decrees in force (the Gazette) with the Decree
// deck and discard north of it; the Action deck and its discard pile beside the Archive; City
// Problems with the problem deck to the right of the Grand Auditor's Office.
// Faces are composed from data (cardFaces.ts).
import * as THREE from 'three';
import { gsap } from 'gsap';
import { TABLE_Y } from './ministry';
import type { CardId, DefId, PlayerView } from '@bcm/rules';
import { cardBack, crispCard, faceTexture, problemFace, spellFace } from './cardFaces';
import { plaqueSprite } from './labels';
import { AMBIENT } from './pacing';
import { SEAL_COLORS, pack, values } from '../game/content';

export type CardPick =
  | { kind: 'spell'; slot: number; card: CardId }
  | { kind: 'problem'; problem: DefId }
  | { kind: 'spellDeck' }
  | { kind: 'problemDeck' }
  | { kind: 'actionDeck' }
  | { kind: 'actionDiscard' }
  | { kind: 'decree'; decree: DefId }
  | { kind: 'decreeDeck' }
  | { kind: 'decreeDiscard' };

export type CardState = 'playable' | 'selected' | 'dim' | null;

const SPELL = { w: 3.4, h: 4.75 };
const PROBLEM = { w: 4.75, h: 3.4 };
const Y = TABLE_Y + 0.03;
const SPELL_X = [-34.8, -31.0];
const PROBLEM_X = 31.2;
/** The Action deck and discard sit on the table level with the Archive, above the City Problems. */
const ACTION_Z = -8.2;
const PROBLEM_TOP = -3.4;
/** The Decrees in force: a 2 × 2 grid north of the spell display, the deck and discard beside it. */
const DECREE_X = [-34.8, -31.0];
const DECREE_Z = [-13.4, -8.3];
const DECREE_PILE_X = -38.6;

interface CardView {
  group: THREE.Group;
  faceMat: THREE.MeshStandardMaterial;
  glow: THREE.Mesh;
  glowMat: THREE.MeshBasicMaterial;
  pulse: gsap.core.Tween | null;
  state: CardState;
  key: string | null;
  home: THREE.Vector3;
}

interface DeckView {
  group: THREE.Group;
  body: THREE.Mesh;
  top: THREE.Mesh;
  label: THREE.Sprite | null;
  count: number;
  size: { w: number; h: number };
  outline?: THREE.Mesh;
}

/** A City Problem's Resolution (P-17): queued this round ("effective next round"), or in force. */
type RibbonState = 'queued' | 'force' | null;

const ribbonTextures = new Map<string, THREE.CanvasTexture>();

/** The sash across a claimed notice: red "effective next round", then brass "in force". */
function ribbonTexture(state: 'queued' | 'force'): THREE.CanvasTexture {
  let t = ribbonTextures.get(state);
  if (t) return t;
  const c = document.createElement('canvas');
  c.width = 1024;
  c.height = 150;
  const g = c.getContext('2d')!;
  const grad = g.createLinearGradient(0, 0, 0, 150);
  if (state === 'queued') {
    grad.addColorStop(0, '#c8403a');
    grad.addColorStop(1, '#7a1a20');
  } else {
    grad.addColorStop(0, '#f2d58c');
    grad.addColorStop(0.5, '#c9a14e');
    grad.addColorStop(1, '#8a6a2c');
  }
  g.fillStyle = grad;
  g.fillRect(0, 12, 1024, 126);
  g.fillStyle = state === 'queued' ? '#f0d38a' : '#5a3d12';
  g.fillRect(0, 18, 1024, 5);
  g.fillRect(0, 127, 1024, 5);
  g.fillStyle = state === 'queued' ? '#fff6e0' : '#2a1606';
  g.font = '700 64px "Cinzel", Georgia, serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  (g as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = '6px';
  g.fillText(state === 'queued' ? 'EFFECTIVE NEXT ROUND' : 'RESOLVED · IN FORCE', 512, 78);
  t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 16;
  ribbonTextures.set(state, t);
  return t;
}

const DECK_NAME = { spell: 'Spell deck', problem: 'Incident file', action: 'Action deck', discard: 'Discard', decree: 'Decree deck', decreeDiscard: 'Struck' } as const;

export class TableCards {
  private spells: CardView[] = [];
  private spellCards: (CardId | null)[] = [];
  private problems = new Map<DefId, CardView & { seals: THREE.Group; claims: string; face: string; ribbon: THREE.Mesh | null; ribbonState: RibbonState }>();
  private problemOrder: DefId[] = [];
  private spellDeck: DeckView;
  private problemDeck: DeckView;
  private actionDeck: DeckView;
  private actionDiscard: DeckView;
  private discardTop: string | null = null;
  private decreeDeck: DeckView;
  private decreeDiscard: DeckView;
  private decreeDiscardTop: string | null = null;
  private decrees = new Map<DefId, CardView & { author: THREE.Mesh }>();
  private gazetteLabel: THREE.Sprite | null = null;
  private gazetteKey = '';
  /** After the first sync, Decrees coming and going are animated (a snapshot just places them). */
  private decreesSeen = false;
  private hovered: THREE.Group | null = null;
  private edgeMat = new THREE.MeshStandardMaterial({ color: '#cdbb93', roughness: 0.9 });
  reducedMotion = false;
  private all: THREE.Object3D[] = [];
  /** Cards mid-animation: sync() leaves their transform alone. */
  private flying = new Set<THREE.Group>();
  /** Lay City Problems out for this many cards (while they are dealt one by one). */
  private layoutN: number | null = null;

  /** Hide everything on the table (the menu backdrop shows an empty Ministry). */
  setVisible(on: boolean): void {
    for (const o of this.all) o.visible = on;
  }

  constructor(private scene: THREE.Scene) {
    this.spellDeck = this.makeDeck('spell', SPELL, this.spellPos(-1));
    this.problemDeck = this.makeDeck('problem', PROBLEM, new THREE.Vector3(PROBLEM_X, Y, 0));
    this.actionDeck = this.makeDeck('action', SPELL, new THREE.Vector3(PROBLEM_X, Y, ACTION_Z));
    this.actionDiscard = this.makeDeck('discard', SPELL, new THREE.Vector3(PROBLEM_X + 5.1, Y, ACTION_Z));
    this.decreeDeck = this.makeDeck('decree', SPELL, new THREE.Vector3(DECREE_PILE_X, Y, DECREE_Z[1]!));
    this.decreeDiscard = this.makeDeck('decreeDiscard', SPELL, new THREE.Vector3(DECREE_PILE_X, Y, DECREE_Z[0]!));
    // The Gazette's empty places: faint outlines on the table.
    for (let i = 0; i < 4; i++) {
      const outline = new THREE.Mesh(new THREE.PlaneGeometry(SPELL.w, SPELL.h), new THREE.MeshBasicMaterial({ color: '#4f8a70', transparent: true, opacity: 0.2, depthWrite: false }));
      outline.rotation.x = -Math.PI / 2;
      outline.position.copy(this.decreePos(i)).setY(Y + 0.01);
      this.scene.add(outline);
      this.all.push(outline);
    }
  }

  /** Gazette slot i (oldest first): left to right, then the row nearer the spells. */
  private decreePos(i: number): THREE.Vector3 {
    return new THREE.Vector3(DECREE_X[i % 2]!, Y, DECREE_Z[Math.min(1, Math.floor(i / 2))]!);
  }

  // ─── Layout ────────────────────────────────────────────────────────────────

  /** Deck is grid slot 0, Arcanium slots follow: 2 columns × 3 rows beside the Arcanium. */
  private spellPos(slot: number): THREE.Vector3 {
    const g = slot + 1;
    const col = g % 2;
    const row = Math.floor(g / 2);
    return new THREE.Vector3(SPELL_X[col]!, Y, -1.4 + row * 5.1);
  }

  private problemGrid(count: number): (i: number) => THREE.Vector3 {
    const n = this.layoutN ?? count;
    const slots = n + 1;
    const cols = slots > 8 ? 3 : 2;
    const rows = Math.ceil(slots / cols);
    const step = 3.75;
    const z0 = Math.max(PROBLEM_TOP, 3.4 - ((rows - 1) * step) / 2);
    return (i: number) => new THREE.Vector3(PROBLEM_X + (i % cols) * 5.1, Y, z0 + Math.floor(i / cols) * step);
  }

  /** Footprints [x0, x1, z0, z1] of the two card areas (for camera framing). */
  footprints(): [number, number, number, number][] {
    const n = Math.max(this.problemOrder.length, 5);
    const grid = this.problemGrid(n);
    const first = grid(0);
    const last = grid(n);
    const cols = n + 1 > 8 ? 3 : 2;
    const s0 = this.spellPos(-1);
    const s1 = this.spellPos(this.spellCards.length ? this.spellCards.length - 1 : 4);
    void s0;
    return [
      [DECREE_PILE_X - SPELL.w / 2 - 0.4, SPELL_X[1]! + SPELL.w / 2, DECREE_Z[0]! - SPELL.h / 2 - 0.3, s1.z + SPELL.h / 2 + 0.4],
      [PROBLEM_X - PROBLEM.w / 2, PROBLEM_X + (cols - 1) * 5.1 + PROBLEM.w / 2 + 0.4, ACTION_Z - SPELL.h / 2 - 0.3, last.z + PROBLEM.h / 2 + 0.4],
    ];
  }

  /** Horizontal extent of everything on the table (for camera framing). */
  extentX(): [number, number] {
    const cols = Math.max(this.problemOrder.length, 5) + 1 > 8 ? 3 : 2;
    return [DECREE_PILE_X - SPELL.w / 2 - 0.6, PROBLEM_X + (cols - 1) * 5.1 + PROBLEM.w / 2 + 0.6];
  }

  // ─── Construction ──────────────────────────────────────────────────────────

  private makeCard(size: { w: number; h: number }, pick: CardPick): CardView {
    const group = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(size.w - 0.12, 0.05, size.h - 0.12), this.edgeMat);
    body.position.y = 0.025;
    body.castShadow = false;
    body.receiveShadow = true;
    const faceMat = crispCard(new THREE.MeshStandardMaterial({ roughness: 0.82, transparent: true, alphaTest: 0.4 }));
    const face = new THREE.Mesh(new THREE.PlaneGeometry(size.w, size.h), faceMat);
    face.rotation.x = -Math.PI / 2;
    face.position.y = 0.052;
    face.receiveShadow = true;
    const glowMat = new THREE.MeshBasicMaterial({ color: '#ffd86b', transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
    const glow = new THREE.Mesh(new THREE.PlaneGeometry(size.w + 0.7, size.h + 0.7), glowMat);
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = 0.01;
    glow.visible = false; // shown only while lit (applyState)
    for (const m of [body, face]) m.userData.pick = pick;
    group.add(glow, body, face);
    this.scene.add(group);
    this.all.push(group);
    return { group, faceMat, glow, glowMat, pulse: null, state: null, key: null, home: new THREE.Vector3() };
  }

  private makeDeck(kind: 'spell' | 'problem' | 'action' | 'discard' | 'decree' | 'decreeDiscard', size: { w: number; h: number }, at: THREE.Vector3): DeckView {
    const group = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(size.w - 0.1, 1, size.h - 0.1), this.edgeMat);
    body.castShadow = false;
    body.receiveShadow = true;
    const faceUp = kind === 'discard' || kind === 'decreeDiscard';
    const top = new THREE.Mesh(new THREE.PlaneGeometry(size.w, size.h), crispCard(new THREE.MeshStandardMaterial({ map: faceUp ? null : cardBack(kind as 'spell' | 'problem' | 'action' | 'decree'), roughness: 0.8, transparent: true, alphaTest: 0.4 })));
    top.rotation.x = -Math.PI / 2;
    const pick: CardPick = {
      spell: { kind: 'spellDeck' as const },
      problem: { kind: 'problemDeck' as const },
      action: { kind: 'actionDeck' as const },
      discard: { kind: 'actionDiscard' as const },
      decree: { kind: 'decreeDeck' as const },
      decreeDiscard: { kind: 'decreeDiscard' as const },
    }[kind];
    body.userData.pick = pick;
    top.userData.pick = pick;
    group.add(body, top);
    group.position.copy(at);
    this.scene.add(group);
    this.all.push(group);
    const deck: DeckView = { group, body, top, label: null, count: -1, size };
    this.setDeckCount(deck, 0, DECK_NAME[kind]);
    if (faceUp) {
      // An empty discard shows as a faint outline on the table.
      const outline = new THREE.Mesh(new THREE.PlaneGeometry(size.w, size.h), new THREE.MeshBasicMaterial({ color: '#c49a52', transparent: true, opacity: 0.18, depthWrite: false }));
      outline.rotation.x = -Math.PI / 2;
      outline.position.y = 0.01;
      outline.userData.pick = pick;
      deck.group.add(outline);
      deck.outline = outline;
    }
    return deck;
  }

  private setDeckCount(deck: DeckView, count: number, name: string): void {
    if (deck.count === count) return;
    deck.count = count;
    const h = Math.max(0.05, Math.min(1.2, count * 0.035));
    deck.body.scale.y = h;
    deck.body.position.y = h / 2;
    deck.top.position.y = h + 0.003;
    deck.top.visible = count > 0;
    if (deck.label) {
      deck.group.remove(deck.label);
      deck.label.material.map?.dispose();
      deck.label.material.dispose();
    }
    deck.label = plaqueSprite({ title: `${name} · ${count}`, dark: true, accent: '#c49a52', width: 520 }, 3.4);
    deck.label.position.set(0, h + 1.1, deck.size.h / 2 + 0.2);
    deck.group.add(deck.label);
  }

  // ─── Sync (reconciliation, no animation) ───────────────────────────────────

  sync(view: PlayerView): void {
    // Arcanium display.
    while (this.spells.length < view.arcanium.length) {
      const slot = this.spells.length;
      this.spells.push(this.makeCard(SPELL, { kind: 'spell', slot, card: '' }));
    }
    this.spellCards = [...view.arcanium];
    view.arcanium.forEach((card, slot) => {
      const cv = this.spells[slot]!;
      cv.home.copy(this.spellPos(slot));
      if (!card) {
        cv.group.visible = false;
        cv.key = null;
        return;
      }
      const def = view.cards[card]!;
      this.setPick(cv, { kind: 'spell', slot, card });
      const key = `${card}`;
      if (cv.key !== key) {
        cv.faceMat.map = spellFace(def, values(def, view));
        cv.faceMat.needsUpdate = true;
        cv.key = key;
      }
      cv.group.visible = true;
      if (this.flying.has(cv.group)) return;
      if (!gsap.isTweening(cv.group.position)) cv.group.position.copy(cv.home).setY(cv.home.y + this.lift(cv.state));
      cv.group.scale.setScalar(1);
      cv.group.rotation.set(0, 0, 0);
    });
    this.setDeckCount(this.spellDeck, view.spellDeckCount, 'Spell deck');

    // City Problems.
    const grid = this.problemGrid(view.problems.length);
    this.problemDeck.group.position.copy(grid(0));
    this.problemOrder = view.problems.map((p) => p.problem);
    for (const [id, pv] of this.problems) {
      if (!this.problemOrder.includes(id)) {
        this.scene.remove(pv.group);
        this.problems.delete(id);
      }
    }
    view.problems.forEach((slot, i) => {
      let pv = this.problems.get(slot.problem);
      if (!pv) {
        const cv = this.makeCard(PROBLEM, { kind: 'problem', problem: slot.problem });
        const seals = new THREE.Group();
        cv.group.add(seals);
        pv = { ...cv, seals, claims: '', face: '', ribbon: null, ribbonState: null };
        this.problems.set(slot.problem, pv);
      }
      // The face shows the Resolution this Problem carries in this game (P-17).
      const face = `${slot.problem}:${slot.resolution ?? ''}`;
      if (pv.face !== face) {
        pv.face = face;
        pv.faceMat.map = problemFace(slot.problem);
        pv.faceMat.needsUpdate = true;
      }
      const want: RibbonState = slot.resolution
        ? view.resolutions?.inForce.some((r) => r.problem === slot.problem)
          ? 'force'
          : view.resolutions?.queued.some((r) => r.problem === slot.problem)
            ? 'queued'
            : null
        : null;
      if (want !== pv.ribbonState) this.setRibbon(pv, want);
      pv.home.copy(grid(i + 1));
      if (!this.flying.has(pv.group) && !gsap.isTweening(pv.group.position)) pv.group.position.copy(pv.home).setY(pv.home.y + this.lift(pv.state));
      const claims = slot.claims.join(',');
      if (pv.claims !== claims) {
        pv.claims = claims;
        pv.seals.clear();
        slot.claims.forEach((seat, k) => {
          const seal = new THREE.Mesh(
            new THREE.CylinderGeometry(0.42, 0.42, 0.12, 24),
            new THREE.MeshStandardMaterial({ color: SEAL_COLORS[seat], roughness: 0.4, metalness: 0.1 }),
          );
          seal.position.set(PROBLEM.w / 2 - 0.6 - k * 1.0, 0.12, -PROBLEM.h / 2 + 0.6);
          seal.castShadow = false;
          pv!.seals.add(seal);
        });
      }
      const closed = slot.claims.length >= (view.options.problemTiers ? 2 : 1);
      pv.faceMat.color.setScalar(closed ? 0.55 : pv.state === 'dim' ? 0.62 : 1);
    });
    this.setDeckCount(this.problemDeck, Math.max(0, pack.problems.length - view.problems.length), 'Incident file');

    // Action deck and the face-up discard pile beside the Archive.
    this.setDeckCount(this.actionDeck, view.actionDeckCount, 'Action deck');
    this.setDeckCount(this.actionDiscard, view.actionDiscard.length, 'Discard');
    const topCard = view.actionDiscard[view.actionDiscard.length - 1];
    const topDef = topCard ? (view.cards[topCard] ?? null) : null;
    if (topDef !== this.discardTop) {
      this.discardTop = topDef;
      const mat = this.actionDiscard.top.material as THREE.MeshStandardMaterial;
      mat.map = topDef ? faceTexture(topDef) : null;
      mat.needsUpdate = true;
    }
    this.actionDiscard.top.visible = !!topDef;
    if (this.actionDiscard.outline) this.actionDiscard.outline.visible = !topDef;

    this.syncDecrees(view);
  }

  // ─── Resolutions (P-17) ────────────────────────────────────────────────────

  private setRibbon(pv: CardView & { ribbon: THREE.Mesh | null; ribbonState: RibbonState }, state: RibbonState): void {
    pv.ribbonState = state;
    if (!state) {
      if (pv.ribbon) pv.ribbon.visible = false;
      return;
    }
    if (!pv.ribbon) {
      const m = new THREE.MeshBasicMaterial({ map: ribbonTexture(state), transparent: true, toneMapped: false, depthWrite: false });
      const ribbon = new THREE.Mesh(new THREE.PlaneGeometry(4.2, 0.6), m);
      ribbon.rotation.x = -Math.PI / 2;
      ribbon.rotation.z = 0.5;
      ribbon.position.set(-PROBLEM.w / 2 + 1.25, 0.09, -PROBLEM.h / 2 + 0.95);
      ribbon.renderOrder = 3;
      pv.group.add(ribbon);
      pv.ribbon = ribbon;
    }
    const m = pv.ribbon.material as THREE.MeshBasicMaterial;
    m.map = ribbonTexture(state);
    m.needsUpdate = true;
    pv.ribbon.visible = true;
  }

  /** ResolutionQueued: the red "effective next round" sash drops onto the claimed notice. */
  markQueued(problem: DefId, speed = 1): Promise<void> {
    const pv = this.problems.get(problem);
    if (!pv) return Promise.resolve();
    this.setRibbon(pv, 'queued');
    const r = pv.ribbon!;
    if (this.reducedMotion) return Promise.resolve();
    return new Promise((resolve) => {
      gsap
        .timeline({ onComplete: resolve })
        .timeScale(speed)
        .fromTo(r.scale, { x: 1.7, y: 1.7, z: 1.7 }, { x: 1, y: 1, z: 1, duration: 0.3, ease: 'power3.in' }, 0)
        .fromTo(r.position, { y: 1.4 }, { y: 0.09, duration: 0.3, ease: 'power3.in' }, 0)
        .to(pv.group.position, { y: pv.group.position.y - 0.08, duration: 0.05, yoyo: true, repeat: 1 }, 0.3);
    });
  }

  /** ResolutionEnacted: the sash turns over to brass — the Resolution is in force. */
  markInForce(problem: DefId, speed = 1): Promise<void> {
    const pv = this.problems.get(problem);
    if (!pv) return Promise.resolve();
    const had = pv.ribbon && pv.ribbonState;
    this.setRibbon(pv, 'force');
    if (!had || this.reducedMotion) return Promise.resolve();
    const r = pv.ribbon!;
    return new Promise((resolve) => {
      gsap
        .timeline({ onComplete: resolve })
        .timeScale(speed)
        .fromTo(r.scale, { y: 0.05 }, { y: 1, duration: 0.35, ease: 'back.out(2)' }, 0);
    });
  }

  /**
   * The Gazette on the table: the Decrees in force, face up, oldest first, each with a wax seal in
   * its author's colour (gilt for the Ministry's own); the Decree deck and the discard beside them.
   * New Decrees come off the deck (or back from the discard); retired ones slide onto the discard.
   */
  private syncDecrees(view: PlayerView): void {
    const inForce = view.decrees ?? [];
    const discard = view.decreeDiscard ?? [];
    const animate = this.decreesSeen && !this.reducedMotion;
    for (const [id, dv] of this.decrees) {
      if (inForce.some((d) => d.decree === id)) continue;
      this.decrees.delete(id);
      const drop = () => {
        this.scene.remove(dv.group);
        this.all.splice(this.all.indexOf(dv.group), 1);
      };
      if (!animate) drop();
      else {
        this.flying.add(dv.group);
        const to = this.decreeDiscard.group.position;
        gsap
          .timeline({ onComplete: () => (this.flying.delete(dv.group), drop()) })
          .to(dv.group.position, { x: to.x, z: to.z, duration: 0.6, ease: 'power2.inOut' }, 0)
          .to(dv.group.position, { y: Y + 1.4, duration: 0.3, ease: 'power2.out', yoyo: true, repeat: 1 }, 0);
      }
    }
    inForce.forEach((d, i) => {
      let dv = this.decrees.get(d.decree);
      const home = this.decreePos(i);
      if (!dv) {
        const cv = this.makeCard(SPELL, { kind: 'decree', decree: d.decree });
        cv.faceMat.map = faceTexture(d.decree);
        cv.faceMat.needsUpdate = true;
        const author = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.12, 24), new THREE.MeshStandardMaterial({ roughness: 0.4, metalness: 0.2 }));
        author.position.set(SPELL.w / 2 - 0.62, 0.12, SPELL.h / 2 - 0.62);
        cv.group.add(author);
        dv = { ...cv, author };
        this.decrees.set(d.decree, dv);
        if (animate) {
          // Off the deck (or back from the discard), flipping face up on the way.
          const from = (discard.includes(d.decree) ? this.decreeDiscard : this.decreeDeck).group.position;
          const g = dv.group;
          this.flying.add(g);
          g.position.set(from.x, Y + 0.8, from.z);
          g.rotation.z = Math.PI;
          gsap
            .timeline({ onComplete: () => void this.flying.delete(g) })
            .to(g.position, { x: home.x, z: home.z, duration: 0.7, ease: 'power2.inOut' }, 0)
            .to(g.position, { y: Y + 2, duration: 0.35, ease: 'power2.out' }, 0)
            .to(g.position, { y: home.y, duration: 0.35, ease: 'power2.in' }, 0.35)
            .to(g.rotation, { z: 0, duration: 0.6, ease: 'power2.inOut' }, 0.05);
        }
      }
      const mat = dv.author.material as THREE.MeshStandardMaterial;
      mat.color.set(d.by === null ? '#d8b05c' : SEAL_COLORS[d.by]!);
      mat.metalness = d.by === null ? 0.8 : 0.1;
      dv.home.copy(home);
      if (!this.flying.has(dv.group) && !gsap.isTweening(dv.group.position)) dv.group.position.copy(home);
    });
    this.decreesSeen = true;

    this.setDeckCount(this.decreeDeck, view.decreeDeckCount ?? 0, DECK_NAME.decree);
    this.setDeckCount(this.decreeDiscard, discard.length, DECK_NAME.decreeDiscard);
    const top = discard[discard.length - 1] ?? null;
    if (top !== this.decreeDiscardTop) {
      this.decreeDiscardTop = top;
      const m = this.decreeDiscard.top.material as THREE.MeshStandardMaterial;
      m.map = top ? faceTexture(top) : null;
      m.needsUpdate = true;
    }
    this.decreeDiscard.top.visible = !!top;
    if (this.decreeDiscard.outline) this.decreeDiscard.outline.visible = !top;

    // One plaque over the Gazette: how many of its places are taken.
    const key = `${inForce.length}/${view.options.decreeSlots}`;
    if (key !== this.gazetteKey) {
      this.gazetteKey = key;
      if (this.gazetteLabel) {
        this.scene.remove(this.gazetteLabel);
        this.gazetteLabel.material.map?.dispose();
        this.gazetteLabel.material.dispose();
        this.all.splice(this.all.indexOf(this.gazetteLabel), 1);
      }
      this.gazetteLabel = plaqueSprite({ title: `Decrees · ${key}`, dark: true, accent: '#4f8a70', width: 520 }, 3.4);
      this.gazetteLabel.position.set((DECREE_X[0]! + DECREE_X[1]!) / 2, Y + 1.2, DECREE_Z[0]! - SPELL.h / 2 - 0.5);
      this.scene.add(this.gazetteLabel);
      this.all.push(this.gazetteLabel);
    }
  }

  /** The face-up card on top of the Decree discard, if any. */
  decreeDiscardTopDef(): string | null {
    return this.decreeDiscardTop;
  }

  /** The face-up card on top of the Action discard pile, if any. */
  discardTopDef(): string | null {
    return this.discardTop;
  }

  private setPick(cv: CardView, pick: CardPick): void {
    cv.group.traverse((o) => {
      if (o.userData.pick) o.userData.pick = pick;
    });
  }

  private lift(state: CardState): number {
    return state === 'selected' ? 0.6 : state === 'playable' ? 0.2 : 0;
  }

  // ─── States & hover ────────────────────────────────────────────────────────

  setStates(spells: Map<CardId, CardState>, problems: Map<DefId, CardState>): void {
    this.spells.forEach((cv, slot) => this.applyState(cv, spells.get(this.spellCards[slot] ?? '') ?? null));
    for (const [id, pv] of this.problems) this.applyState(pv, problems.get(id) ?? null);
  }

  private applyState(cv: CardView, state: CardState): void {
    if (cv.state === state) return;
    cv.state = state;
    cv.pulse?.kill();
    cv.pulse = null;
    cv.faceMat.color.setScalar(state === 'dim' ? 0.62 : 1);
    cv.glowMat.color.set(state === 'selected' ? '#fff4c4' : '#ffd86b');
    if (state === 'playable' && !this.reducedMotion) {
      cv.glowMat.opacity = 0.35;
      cv.pulse = gsap.to(cv.glowMat, { opacity: 0.9, duration: 0.8, yoyo: true, repeat: -1, ease: 'sine.inOut', data: AMBIENT });
    } else cv.glowMat.opacity = state === 'selected' ? 0.9 : state === 'playable' ? 0.7 : 0;
    cv.glow.visible = cv.glowMat.opacity > 0; // unlit glows are hidden, not drawn
    gsap.to(cv.group.position, { y: cv.home.y + this.lift(state), duration: 0.18, ease: 'power2.out' });
  }

  hover(group: THREE.Group | null): void {
    if (group === this.hovered) return;
    const prev = this.hovered;
    this.hovered = group;
    const base = (g: THREE.Group) => {
      const cv = [...this.spells, ...this.problems.values(), ...this.decrees.values()].find((c) => c.group === g);
      return cv ? cv.home.y + this.lift(cv.state) : Y;
    };
    if (prev) gsap.to(prev.position, { y: base(prev), duration: 0.15 });
    if (group) gsap.to(group.position, { y: base(group) + 0.35, duration: 0.15, ease: 'power2.out' });
  }

  pickables(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    for (const cv of [...this.spells, ...this.problems.values(), ...this.decrees.values()]) if (cv.group.visible) cv.group.traverse((o) => o.userData.pick && out.push(o));
    for (const d of [this.spellDeck, this.problemDeck, this.actionDeck, this.actionDiscard, this.decreeDeck, this.decreeDiscard]) {
      out.push(d.body);
      if (d.top.visible) out.push(d.top);
      if (d.outline?.visible) out.push(d.outline);
    }
    return out;
  }

  groupOf(obj: THREE.Object3D): THREE.Group | null {
    let o: THREE.Object3D | null = obj;
    while (o && o.parent !== this.scene) o = o.parent;
    return (o as THREE.Group) ?? null;
  }

  anchor(pick: CardPick): THREE.Vector3 {
    if (pick.kind === 'spell') return this.spellPos(pick.slot).setY(0.5);
    if (pick.kind === 'spellDeck') return this.spellDeck.group.position.clone().setY(0.8);
    if (pick.kind === 'problemDeck') return this.problemDeck.group.position.clone().setY(0.8);
    if (pick.kind === 'actionDeck') return this.actionDeck.group.position.clone().setY(0.8);
    if (pick.kind === 'actionDiscard') return this.actionDiscard.group.position.clone().setY(0.5);
    if (pick.kind === 'decreeDeck') return this.decreeDeck.group.position.clone().setY(0.8);
    if (pick.kind === 'decreeDiscard') return this.decreeDiscard.group.position.clone().setY(0.5);
    if (pick.kind === 'decree') return this.decrees.get(pick.decree)?.group.position.clone().setY(0.5) ?? this.decreePos(0).setY(0.5);
    return this.problems.get(pick.problem)?.group.position.clone().setY(0.5) ?? new THREE.Vector3(27, 0, 4);
  }

  // ─── Animations ────────────────────────────────────────────────────────────

  /** A new spell is dealt from the deck into a display slot, flipping face-up on the way. */
  deal(slot: number, card: CardId, def: DefId, view: PlayerView, speed = 1): Promise<void> {
    const cv = this.spells[slot];
    if (!cv) return Promise.resolve();
    cv.faceMat.map = spellFace(def, values(def, view));
    cv.faceMat.needsUpdate = true;
    cv.key = card;
    this.setPick(cv, { kind: 'spell', slot, card });
    this.spellCards[slot] = card;
    const from = this.spellDeck.group.position.clone().setY(this.spellDeck.body.scale.y + 0.1);
    const to = this.spellPos(slot);
    cv.home.copy(to);
    return this.flyFromDeck(cv.group, from, to, speed);
  }

  /** A card leaves the top of a deck, arcs to its place and flips face-up on the way. */
  private flyFromDeck(group: THREE.Group, from: THREE.Vector3, to: THREE.Vector3, speed: number): Promise<void> {
    this.flying.add(group);
    group.visible = true;
    group.position.copy(from);
    group.rotation.set(0, 0, Math.PI);
    group.scale.setScalar(1);
    const p = { t: 0 };
    return new Promise((resolve) => {
      gsap
        .timeline({
          onComplete: () => {
            this.flying.delete(group);
            resolve();
          },
        })
        .timeScale(speed)
        .to(p, {
          t: 1,
          duration: 0.45,
          ease: 'power2.inOut',
          onUpdate: () => {
            group.position.lerpVectors(from, to, p.t);
            group.position.y += 2.2 * 4 * p.t * (1 - p.t);
          },
        })
        .to(group.rotation, { z: 0, duration: 0.4, ease: 'power2.inOut' }, 0.05);
    });
  }

  /** Deal a revealed City Problem from the incident file to its place (it must already be synced). */
  dealProblem(problem: DefId, speed = 1): Promise<void> {
    const pv = this.problems.get(problem);
    if (!pv) return Promise.resolve();
    const from = this.problemDeck.group.position.clone().setY(this.problemDeck.body.scale.y + 0.1);
    return this.flyFromDeck(pv.group, from, pv.home.clone(), speed);
  }

  /** Fix the City Problem layout while problems are dealt one at a time (null: follow the view). */
  setProblemLayout(n: number | null): void {
    this.layoutN = n;
  }

  /**
   * Opening shuffle: each deck is split in two, riffled back together and squared up, twice. The
   * riffling cards are temporary card backs laid on top of the deck.
   */
  shuffle(speed = 1): Promise<void> {
    const decks = [this.spellDeck, this.problemDeck, this.actionDeck].filter((d) => d.count > 0);
    if (!decks.length || this.reducedMotion) return Promise.resolve();
    const tl = gsap.timeline({ paused: true });
    const temps: THREE.Mesh[] = [];
    const geos: THREE.BufferGeometry[] = [];
    decks.forEach((d, di) => {
      const back = (d.top.material as THREE.MeshStandardMaterial).map;
      const geo = new THREE.PlaneGeometry(d.size.w, d.size.h);
      geos.push(geo);
      const mat = crispCard(new THREE.MeshStandardMaterial({ map: back, roughness: 0.8 }));
      const top = d.body.scale.y;
      const n = 10;
      const rest = (i: number) => top + 0.01 + i * 0.01;
      const cards = Array.from({ length: n }, (_, i) => {
        const m = new THREE.Mesh(geo, mat);
        m.rotation.x = -Math.PI / 2;
        m.position.set(0, rest(i), 0);
        m.castShadow = false;
        d.group.add(m);
        temps.push(m);
        return m;
      });
      for (let r = 0; r < 2; r++) {
        const t0 = di * 0.08 + r * 0.95;
        cards.forEach((m, i) => {
          const side = i < n / 2 ? -1 : 1; // cut: bottom half left, top half right
          const k = i < n / 2 ? i : i - n / 2;
          tl.to(m.position, { x: side * d.size.w * 0.58, y: top + 0.35 + k * 0.03, duration: 0.26, ease: 'power2.out' }, t0 + k * 0.01);
          tl.to(m.rotation, { z: side * 0.1, duration: 0.26, ease: 'power2.out' }, t0 + k * 0.01);
          // Riffle: the halves interleave, alternating sides.
          const order = k * 2 + (side < 0 ? 0 : 1);
          tl.to(m.position, { x: 0, y: rest(order), duration: 0.2, ease: 'power2.in' }, t0 + 0.34 + order * 0.028);
          tl.to(m.rotation, { z: 0, duration: 0.2, ease: 'power2.in' }, t0 + 0.34 + order * 0.028);
        });
        tl.to(d.group.scale, { x: 1.04, z: 1.04, duration: 0.07, yoyo: true, repeat: 1 }, t0 + 0.34 + n * 0.028 + 0.18);
      }
    });
    return new Promise((resolve) => {
      tl.eventCallback('onComplete', () => {
        for (const m of temps) m.removeFromParent();
        for (const g of geos) g.dispose();
        resolve();
      });
      tl.timeScale(speed).play();
    });
  }

  /** Upkeep: the whole display is swept off the counter to the discard. */
  clear(speed = 1): Promise<void> {
    const visible = this.spells.filter((cv) => cv.group.visible);
    if (!visible.length) return Promise.resolve();
    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: () => {
          for (const cv of visible) cv.group.visible = false;
          resolve();
        },
      });
      tl.timeScale(speed);
      visible.forEach((cv, i) => {
        tl.to(cv.group.position, { x: cv.group.position.x - 7, y: 1.5, duration: 0.35, ease: 'power2.in' }, i * 0.05);
        tl.to(cv.group.scale, { x: 0.3, y: 0.3, z: 0.3, duration: 0.35, ease: 'power2.in' }, i * 0.05);
      });
    });
  }

  /** A purchased spell lifts off the counter (then a DOM token flies it to the buyer's hand). */
  take(slot: number, speed = 1): Promise<void> {
    const cv = this.spells[slot];
    if (!cv || !cv.group.visible) return Promise.resolve();
    return new Promise((resolve) => {
      gsap
        .timeline({
          onComplete: () => {
            cv.group.visible = false;
            resolve();
          },
        })
        .timeScale(speed)
        .to(cv.group.position, { y: 3, duration: 0.3, ease: 'power2.out' })
        .to(cv.group.scale, { x: 0.1, y: 0.1, z: 0.1, duration: 0.2, ease: 'power2.in' }, 0.15);
    });
  }
}
